import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import {
  authAPI,
  dhis2ConfigAPI,
  userAccessAPI,
  type Dhis2ConfigOut,
  type PermissionCatalogItem,
  type TenantRoleOut,
  type UserAccessRow,
} from '../../services/api';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../api/client';

const AUTH_STORAGE_KEY = 'intellicare_auth';

function normalizeAccess(raw: unknown) {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  return {
    can_open_settings: a.can_open_settings !== false,
    can_open_test_dhis2: a.can_open_test_dhis2 !== false,
    can_open_admin_portal: a.can_open_admin_portal !== false,
    allowed_main_tabs: Array.isArray(a.allowed_main_tabs) ? (a.allowed_main_tabs as string[]) : null,
    allowed_config_ids: Array.isArray(a.allowed_config_ids) ? (a.allowed_config_ids as string[]) : null,
    config_scope_restricted: Boolean(a.config_scope_restricted),
  };
}

function initials(displayName: string, username: string) {
  const s = (displayName || username || '?').trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function formatLastLogin(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
}

function samePermissionSet(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

function isCustomTenantRole(name: string | null | undefined) {
  return !!(name && String(name).startsWith('uam_custom_u_'));
}

function roleChipColor(tenantRole: string | null | undefined) {
  const n = (tenantRole || '').toLowerCase();
  if (n.includes('tenant_admin') || n === 'admin') return { bgcolor: '#ede9fe', color: '#5b21b6' };
  if (n.includes('doctor')) return { bgcolor: '#dbeafe', color: '#1d4ed8' };
  return { bgcolor: '#f3f4f6', color: '#374151' };
}

function configStub(id: string): Dhis2ConfigOut {
  return {
    id,
    name: `${id.slice(0, 8)}…`,
    schema_source: 'admin_defined',
    form_schema_json: null,
    field_mappings_json: null,
    extraction_prompt_text: null,
    config_type: null,
  } as Dhis2ConfigOut;
}

const STEPS = ['Basic info', 'Role & flags', 'Entity access', 'Permissions'];

/**
 * Settings opens inside App’s full-screen `Dialog` (z-index ≈ theme.zIndex.modal).
 * `Drawer` defaults to theme.zIndex.drawer (lower) and would render behind — bump overlays above the shell.
 */
function zAboveSettingsShell(theme: Theme) {
  return theme.zIndex.modal + 50;
}

/** Confirm dialogs opened while the user drawer is visible must sit above the drawer modal. */
function zAboveUserDrawer(theme: Theme) {
  return theme.zIndex.modal + 150;
}

/**
 * Select / Autocomplete menus default to `zIndex.modal` (1300) and sit under the Settings `Dialog`, the User
 * `Drawer` (modal+50), and nested confirm `Dialog`s (modal+150). Use the tooltip layer so lists stay visible.
 */
function zSelectAutocompleteOverDrawer(theme: Theme) {
  return theme.zIndex.tooltip;
}

const overlaySelectMenuProps = {
  MenuProps: {
    disableScrollLock: true,
    sx: (theme: Theme) => ({ zIndex: zSelectAutocompleteOverDrawer(theme) }),
  },
};

const overlayAutocompleteSlotProps = {
  componentsProps: {
    popper: {
      sx: (theme: Theme) => ({ zIndex: zSelectAutocompleteOverDrawer(theme) }),
    },
  },
};

export default function UserAccessManagement() {
  const { activeTenantId, tenants, setActiveTenantId } = useBusiness();
  const { user: authUser } = useAuth();
  const isAppAdmin = (authUser?.role || '').toLowerCase() === 'admin';

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [includeLegacyDoctors, setIncludeLegacyDoctors] = useState(false);
  const [showRemovedFromTenant, setShowRemovedFromTenant] = useState(false);
  const [rows, setRows] = useState<UserAccessRow[]>([]);
  const [configs, setConfigs] = useState<Dhis2ConfigOut[]>([]);
  const [entityConfigsMap, setEntityConfigsMap] = useState<Record<string, Dhis2ConfigOut[]>>({});
  const [tenantEntities, setTenantEntities] = useState<Array<{ entity_id: string; name: string; type?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterEntity, setFilterEntity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Record<number, Set<string>>>({});

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<UserAccessRow | null>(null);

  const [permCatalog, setPermCatalog] = useState<PermissionCatalogItem[]>([]);
  const [tenantRoles, setTenantRoles] = useState<TenantRoleOut[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [activeStep, setActiveStep] = useState(0);
  const [editRow, setEditRow] = useState<UserAccessRow | null>(null);

  const [duUsername, setDuUsername] = useState('');
  const [duPassword, setDuPassword] = useState('');
  const [duDisplayName, setDuDisplayName] = useState('');
  const [duActive, setDuActive] = useState(true);
  const [duCanSettings, setDuCanSettings] = useState(true);
  const [duCanTest, setDuCanTest] = useState(true);
  const [duCanAdminPortal, setDuCanAdminPortal] = useState(true);
  const [duTenantRoleName, setDuTenantRoleName] = useState('doctor');
  const [duPermEnabled, setDuPermEnabled] = useState<Record<string, boolean>>({});
  const [duEntityId, setDuEntityId] = useState('');
  const [duConfigIds, setDuConfigIds] = useState<Dhis2ConfigOut[]>([]);
  const [duAccessAllConfigs, setDuAccessAllConfigs] = useState(false);

  const [addEntityId, setAddEntityId] = useState('');
  const [addConfigIds, setAddConfigIds] = useState<Dhis2ConfigOut[]>([]);
  const [addAccessAll, setAddAccessAll] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<UserAccessRow | null>(null);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateRow, setReactivateRow] = useState<UserAccessRow | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkRow, setLinkRow] = useState<UserAccessRow | null>(null);
  const [linkEntityId, setLinkEntityId] = useState('');
  const [linkRoleName, setLinkRoleName] = useState<'doctor' | 'staff' | 'tenant_admin'>('doctor');
  const [linkConfigIds, setLinkConfigIds] = useState<Dhis2ConfigOut[]>([]);
  const [clearAssignmentOpen, setClearAssignmentOpen] = useState(false);
  const [pendingRemovalIds, setPendingRemovalIds] = useState<Set<string>>(new Set());
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdRow, setResetPwdRow] = useState<UserAccessRow | null>(null);
  const [resetPwdValue, setResetPwdValue] = useState('');

  const activeTenant = tenants.find((t) => t.tenant_id === selectedTenantId) ?? null;
  const entities = tenantEntities.length
    ? tenantEntities.map((e) => ({ ...e, configs: [] as Array<{ config_id: string; name?: string }> }))
    : (activeTenant?.entities ?? []);

  const entityNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of tenantEntities) m.set(e.entity_id, e.name);
    for (const t of tenants) {
      for (const e of t.entities ?? []) m.set(e.entity_id, e.name);
    }
    return m;
  }, [tenantEntities, tenants]);

  const configById = useMemo(() => {
    const m = new Map<string, Dhis2ConfigOut>();
    for (const c of configs) m.set(c.id, c);
    return m;
  }, [configs]);

  /** Keep primary-entity config chips in sync with API row (used on open and after inline adds). */
  const syncPrimaryEntityConfigsFromRow = useCallback((row: UserAccessRow, primaryEntityId: string) => {
    const asg = row.assignments ?? [];
    const hasAll = asg.some((x) => x.entity_id === primaryEntityId && !x.config_id);
    const cfgs: Dhis2ConfigOut[] = [];
    for (const x of asg) {
      if (x.entity_id !== primaryEntityId) continue;
      if (x.config_id) cfgs.push(configById.get(x.config_id) ?? configStub(x.config_id));
    }
    setDuAccessAllConfigs(hasAll);
    setDuConfigIds(hasAll ? [] : cfgs);
  }, [configById]);

  useEffect(() => {
    if (!tenants.length) {
      setSelectedTenantId(null);
      return;
    }
    setSelectedTenantId((prev) => {
      if (prev && tenants.some((t) => t.tenant_id === prev)) return prev;
      const fromHeader =
        activeTenantId && tenants.some((t) => t.tenant_id === activeTenantId) ? activeTenantId : null;
      return fromHeader ?? tenants[0].tenant_id;
    });
  }, [tenants, activeTenantId]);

  const load = useCallback(async () => {
    if (!selectedTenantId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    const includeInactiveTenantMemberships = showRemovedFromTenant || filterStatus === 'removed';
    try {
      const res = await userAccessAPI.listUsers(
        selectedTenantId,
        includeLegacyDoctors,
        includeInactiveTenantMemberships,
      );
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e as Error)?.message ||
        'Failed to load users';
      setError(String(msg));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, includeLegacyDoctors, showRemovedFromTenant, filterStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void dhis2ConfigAPI.listConfigs().then((res) => {
      if (!cancelled) setConfigs(Array.isArray(res.data) ? res.data : []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTenantId) {
      setTenantEntities([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/business/entities?tenant_id=${encodeURIComponent(selectedTenantId)}`, {
          method: 'GET',
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json().catch(() => []);
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setTenantEntities(list.map((x) => ({ entity_id: String(x.entity_id), name: String(x.name ?? x.entity_id), type: x.type })));
        }
      } catch {
        if (!cancelled) setTenantEntities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  const loadMeta = useCallback(async () => {
    if (!selectedTenantId) return;
    try {
      const [cRes, rRes] = await Promise.all([
        userAccessAPI.getPermissionsCatalog(selectedTenantId),
        userAccessAPI.getTenantRoles(selectedTenantId),
      ]);
      setPermCatalog(Array.isArray(cRes.data) ? cRes.data : []);
      setTenantRoles(Array.isArray(rRes.data) ? rRes.data : []);
    } catch {
      setPermCatalog([]);
      setTenantRoles([]);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  const loadEntityConfigs = useCallback(
    async (eid: string) => {
      if (!selectedTenantId || !eid) return;
      if (entityConfigsMap[eid]) return;
      try {
        const res = await userAccessAPI.listEntityConfigs(selectedTenantId, eid);
        const list = (Array.isArray(res.data) ? res.data : []).map((x) => ({
          id: x.config_id,
          name: x.name,
          schema_source: x.schema_source ?? 'admin_defined',
          form_schema_json: null,
          field_mappings_json: null,
          extraction_prompt_text: null,
          config_type: x.config_type ?? null,
        })) as Dhis2ConfigOut[];
        setEntityConfigsMap((prev) => ({ ...prev, [eid]: list }));
      } catch {
        setEntityConfigsMap((prev) => ({ ...prev, [eid]: [] }));
      }
    },
    [selectedTenantId, entityConfigsMap],
  );

  useEffect(() => {
    setEntityConfigsMap({});
  }, [selectedTenantId]);

  useEffect(() => {
    if (duEntityId) void loadEntityConfigs(duEntityId);
  }, [duEntityId, loadEntityConfigs]);
  useEffect(() => {
    if (addEntityId) void loadEntityConfigs(addEntityId);
  }, [addEntityId, loadEntityConfigs]);
  useEffect(() => {
    if (linkEntityId) void loadEntityConfigs(linkEntityId);
  }, [linkEntityId, loadEntityConfigs]);

  const duEntityConfigs = duEntityId ? (entityConfigsMap[duEntityId] ?? []) : [];
  const addEntityConfigs = addEntityId ? (entityConfigsMap[addEntityId] ?? []) : [];
  const linkEntityConfigs = linkEntityId ? (entityConfigsMap[linkEntityId] ?? []) : [];

  useEffect(() => {
    const allowed = new Set(linkEntityConfigs.map((c) => c.id));
    setLinkConfigIds((prev) => prev.filter((c) => allowed.has(c.id)));
  }, [linkEntityId, linkEntityConfigs]);

  const applyRoleTemplateToPerms = useCallback(
    (roleName: string) => {
      const r = tenantRoles.find((x) => x.name.toLowerCase() === roleName.toLowerCase());
      const next: Record<string, boolean> = {};
      for (const p of permCatalog) next[p.code] = !!(r && r.permission_codes.includes(p.code));
      setDuPermEnabled(next);
    },
    [tenantRoles, permCatalog],
  );

  useEffect(() => {
    if (!drawerOpen || drawerMode !== 'add' || activeStep !== 3) return;
    if (!permCatalog.length) return;
    if (Object.keys(duPermEnabled).length === 0) applyRoleTemplateToPerms(duTenantRoleName);
  }, [drawerOpen, drawerMode, activeStep, permCatalog, duTenantRoleName, duPermEnabled, applyRoleTemplateToPerms]);

  const openAddDrawer = () => {
    setDrawerMode('add');
    setEditRow(null);
    setActiveStep(0);
    setDuUsername('');
    setDuPassword('');
    setDuDisplayName('');
    setDuActive(true);
    setDuCanSettings(true);
    setDuCanTest(true);
    setDuCanAdminPortal(true);
    setDuTenantRoleName('doctor');
    setDuPermEnabled({});
    setDuEntityId(entities[0]?.entity_id ?? '');
    setDuConfigIds([]);
    setDuAccessAllConfigs(false);
    setPendingRemovalIds(new Set());
    setDrawerOpen(true);
    void loadMeta();
  };

  const openEditDrawer = (r: UserAccessRow) => {
    if (r.legacy_no_tenant_membership) return;
    setDrawerMode('edit');
    setEditRow(r);
    setActiveStep(0);
    setDuUsername(r.username);
    setDuPassword('');
    setDuDisplayName(r.display_name);
    setDuActive(r.is_active);
    setDuCanSettings(r.can_open_settings);
    setDuCanTest(r.can_open_test_dhis2);
    setDuCanAdminPortal(r.can_open_admin_portal !== false);
    const tr = r.tenant_role_name || 'doctor';
    setDuTenantRoleName(isCustomTenantRole(tr) ? 'Custom' : tr);
    const pe: Record<string, boolean> = {};
    for (const p of permCatalog) pe[p.code] = (r.permission_codes ?? []).includes(p.code);
    setDuPermEnabled(pe);
    const asg = r.assignments ?? [];
    const useEntity = asg[0]?.entity_id ?? entities[0]?.entity_id ?? '';
    setDuEntityId(useEntity);

    // below code commented dated: 2026-04-09 
    // const hasAll = asg.some((x) => x.entity_id === useEntity && !x.config_id);
    // const cfgs: Dhis2ConfigOut[] = [];
    // for (const x of asg) {
    //   if (x.entity_id !== useEntity) continue;
    //   if (x.config_id) cfgs.push(configById.get(x.config_id) ?? configStub(x.config_id));
    // }
    // setDuConfigIds(hasAll ? [] : cfgs);
    // setDuAccessAllConfigs(hasAll);

    syncPrimaryEntityConfigsFromRow(r, useEntity);
    setAddEntityId(entities[0]?.entity_id ?? '');
    setAddConfigIds([]);
    setAddAccessAll(false);
    setPendingRemovalIds(new Set());
    setDrawerOpen(true);
    void loadMeta();
  };

  useEffect(() => {
    if (!drawerOpen || drawerMode !== 'edit' || !editRow) return;
    if (!permCatalog.length) return;
    const pe: Record<string, boolean> = {};
    for (const p of permCatalog) pe[p.code] = (editRow.permission_codes ?? []).includes(p.code);
    setDuPermEnabled(pe);
  }, [drawerOpen, drawerMode, editRow?.user_id, permCatalog, editRow]);

  const duConfigOptions = useMemo(() => {
    const byId = new Map<string, Dhis2ConfigOut>();
    for (const c of duEntityConfigs) byId.set(c.id, c);
    for (const v of duConfigIds) {
      if (!byId.has(v.id)) byId.set(v.id, v);
    }
    return Array.from(byId.values()).sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  }, [duEntityConfigs, duConfigIds]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.username.toLowerCase().includes(q) ||
          r.display_name.toLowerCase().includes(q) ||
          String(r.user_id).includes(q),
      );
    }
    if (filterRole !== 'all') {
      list = list.filter((r) => (r.tenant_role_name || '').toLowerCase().includes(filterRole.toLowerCase()));
    }
    if (filterEntity !== 'all') {
      list = list.filter((r) => (r.assignments ?? []).some((a) => a.entity_id === filterEntity));
    }
    if (filterStatus === 'active') list = list.filter((r) => r.is_active);
    if (filterStatus === 'inactive') list = list.filter((r) => !r.is_active);
    if (filterStatus === 'removed') list = list.filter((r) => r.tenant_membership_active === false);
    return list;
  }, [rows, search, filterRole, filterEntity, filterStatus]);

  const pagedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  const resolvePermissionsPayload = () => {
    const codes = permCatalog.filter((p) => duPermEnabled[p.code]).map((p) => p.code);
    if (duTenantRoleName === 'Custom') return { permission_codes: codes };
    const template = tenantRoles.find((x) => x.name.toLowerCase() === duTenantRoleName.toLowerCase());
    if (template && samePermissionSet(codes, template.permission_codes)) {
      return { tenant_role_name: duTenantRoleName };
    }
    return { permission_codes: codes };
  };

  const saveDrawer = async () => {
    if (!selectedTenantId) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const permPayload = resolvePermissionsPayload();
      if (drawerMode === 'add') {
        if (!duUsername.trim() || !duPassword.trim() || !duDisplayName.trim() || !duEntityId) {
          setError('Username, password, display name, and entity are required.');
          setLoading(false);
          return;
        }
        const cfgPayload =
          duAccessAllConfigs || duConfigIds.length === 0 ? [] : duConfigIds.map((c) => c.id);
        const perm = permPayload as { tenant_role_name?: string; permission_codes?: string[] };
        await userAccessAPI.createUser({
          username: duUsername.trim(),
          password: duPassword.trim(),
          display_name: duDisplayName.trim(),
          tenant_id: selectedTenantId,
          entity_id: duEntityId,
          config_ids: cfgPayload,
          role: 'doctor',
          can_open_settings: duCanSettings,
          can_open_test_dhis2: duCanTest,
          can_open_admin_portal: duCanAdminPortal,
          ...(perm.permission_codes !== undefined
            ? { permission_codes: perm.permission_codes }
            : { tenant_role_name: duTenantRoleName === 'Custom' ? 'doctor' : duTenantRoleName }),
        });
        setSuccess('User created.');
      } else if (editRow) {
        const body: Record<string, unknown> = {
          display_name: duDisplayName.trim(),
          is_active: duActive,
          can_open_settings: duCanSettings,
          can_open_test_dhis2: duCanTest,
          can_open_admin_portal: duCanAdminPortal,
        };
        if (duPassword.trim()) body.password = duPassword.trim();
        await userAccessAPI.patchUser(editRow.user_id, selectedTenantId, body);
        await userAccessAPI.patchUser(editRow.user_id, selectedTenantId, {
          ...permPayload,
        } as Record<string, unknown>);
        const cfgList = duAccessAllConfigs || duConfigIds.length === 0 ? [] : duConfigIds.map((c) => c.id);
        await userAccessAPI.patchUser(editRow.user_id, selectedTenantId, {
          entity_id: duEntityId,
          config_ids: cfgList,
        });
        setSuccess('User updated.');
      }
      setDrawerOpen(false);
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e as Error)?.message ||
        'Save failed';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const removeAssignmentsBulk = async (userId: number, ids: string[]) => {
    if (!selectedTenantId || !ids.length) return;
    setLoading(true);
    setError(null);
    try {
      await userAccessAPI.removeAssignments(userId, selectedTenantId, ids);
      setSuccess('Removed selected assignments.');
      setSelectedAssignmentIds((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      await load();
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const addEntityAccess = async () => {
    if (!selectedTenantId || !editRow || !addEntityId) return;
    setLoading(true);
    setError(null);
    try {
      const cfgList = addAccessAll || addConfigIds.length === 0 ? [] : addConfigIds.map((c) => c.id);
      await userAccessAPI.patchUser(editRow.user_id, selectedTenantId, {
        entity_id: addEntityId,
        config_ids: cfgList,
      });
      setSuccess('Entity access added.');
      await load();
      const listRes = await userAccessAPI.listUsers(
        selectedTenantId,
        includeLegacyDoctors,
        showRemovedFromTenant || filterStatus === 'removed',
      );
      const refreshed = (Array.isArray(listRes.data) ? listRes.data : []).find((x) => x.user_id === editRow.user_id);
      // if (refreshed) setEditRow(refreshed);      
      if (refreshed) {
        setEditRow(refreshed);
        // addEntityAccess PATCH updates the server, but saveDrawer re-sends duEntityId + duConfigIds and
        // replaces all rows for that entity — keep primary form state aligned when both pickers target the same entity.
        if (addEntityId === duEntityId) {
          syncPrimaryEntityConfigsFromRow(refreshed, duEntityId);
        }
      }
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedTenantId || !deleteRow) return;
    setLoading(true);
    setError(null);
    try {
      await userAccessAPI.deleteUser(deleteRow.user_id, selectedTenantId);
      setSuccess(`User ${deleteRow.username} removed from tenant.`);
      setDeleteOpen(false);
      setDeleteRow(null);
      await load();
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const confirmReactivate = async () => {
    if (!selectedTenantId || !reactivateRow) return;
    setLoading(true);
    setError(null);
    try {
      await userAccessAPI.reactivateUser(reactivateRow.user_id, selectedTenantId);
      setSuccess(`User ${reactivateRow.username} restored in this tenant.`);
      setReactivateOpen(false);
      setReactivateRow(null);
      await load();
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const confirmLinkLegacy = async () => {
    if (!selectedTenantId || !linkRow || !linkEntityId) return;
    setLoading(true);
    setError(null);
    try {
      await userAccessAPI.linkLegacyUser(linkRow.user_id, {
        tenant_id: selectedTenantId,
        entity_id: linkEntityId,
        config_ids: linkConfigIds.map((c) => c.id),
        role_name: linkRoleName,
      });
      setSuccess(`Linked ${linkRow.username} to tenant.`);
      setLinkOpen(false);
      setLinkRow(null);
      setLinkConfigIds([]);
      await load();
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const confirmClearEntityAssignment = async () => {
    if (!selectedTenantId || !editRow || !duEntityId) return;
    setLoading(true);
    setError(null);
    try {
      await userAccessAPI.patchUser(editRow.user_id, selectedTenantId, {
        entity_id: duEntityId,
        clear_entity_assignments: true,
      });
      setSuccess('Entity assignment removed.');
      setClearAssignmentOpen(false);
      await load();
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const applyPendingRemovals = async () => {
    if (!selectedTenantId || !editRow || !pendingRemovalIds.size) return;
    await removeAssignmentsBulk(editRow.user_id, Array.from(pendingRemovalIds));
    setPendingRemovalIds(new Set());
    const listRes = await userAccessAPI.listUsers(selectedTenantId, includeLegacyDoctors, showRemovedFromTenant || filterStatus === 'removed');
    const refreshed = (Array.isArray(listRes.data) ? listRes.data : []).find((x) => x.user_id === editRow.user_id);
    if (refreshed) setEditRow(refreshed);
  };

  const submitResetPassword = async () => {
    if (!selectedTenantId || !resetPwdRow || !resetPwdValue.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await userAccessAPI.patchUser(resetPwdRow.user_id, selectedTenantId, { password: resetPwdValue.trim() });
      setSuccess('Password updated.');
      setResetPwdOpen(false);
      setResetPwdRow(null);
      setResetPwdValue('');
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (userId: number) => {
    setMenuAnchor(null);
    setError(null);
    try {
      const res = await authAPI.impersonate(userId);
      const d = res.data;
      const authUser = {
        id: d.user.id,
        username: d.user.username,
        displayName: d.user.displayName || d.user.username,
        role: d.user.role,
        doctor_id: d.user.doctor_id ?? null,
      };
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({ token: d.access_token, user: authUser, access: normalizeAccess(d.access) }),
      );
      window.location.reload();
    } catch (e: unknown) {
      setError(String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || e));
    }
  };

  const toggleAssignmentSelect = (userId: number, aid: string) => {
    setSelectedAssignmentIds((prev) => {
      const cur = new Set(prev[userId] ?? []);
      if (cur.has(aid)) cur.delete(aid);
      else cur.add(aid);
      return { ...prev, [userId]: cur };
    });
  };

  const groupedCatalog = useMemo(() => {
    const g: Record<string, PermissionCatalogItem[]> = { CONFIG: [], ENTITY: [], SYSTEM: [] };
    for (const p of permCatalog) {
      const k = p.group === 'CONFIG' || p.group === 'ENTITY' ? p.group : 'SYSTEM';
      g[k].push(p);
    }
    return g;
  }, [permCatalog]);

  const templateRoleOptions = useMemo(() => {
    const names = tenantRoles.map((r) => r.name);
    if (!names.includes('Custom')) return [...names, 'Custom'];
    return names;
  }, [tenantRoles]);

  if (!tenants.length) {
    return (
      <Alert severity="info">
        No tenants are available for your login. Ask an administrator to assign you to a tenant in RBAC, or use an admin
        account.
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        User management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Directory, tenant roles, entity/config assignments, and clinical feature flags. Use the tenant control below; it
        stays in sync with the app header when you change it.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            select
            label="Tenant"
            size="small"
            value={selectedTenantId ?? ''}
            onChange={(e) => {
              const tid = String(e.target.value || '');
              setSelectedTenantId(tid || null);
              setActiveTenantId(tid || null);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
            SelectProps={overlaySelectMenuProps}
          >
            {tenants.map((t) => (
              <MenuItem key={t.tenant_id} value={t.tenant_id}>
                {t.name || t.tenant_id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search users"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220, flex: '1 1 200px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={filterRole}
              onChange={(e: SelectChangeEvent) => {
                setFilterRole(e.target.value);
                setPage(0);
              }}
              MenuProps={overlaySelectMenuProps.MenuProps}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="doctor">Doctor</MenuItem>
              <MenuItem value="tenant_admin">Tenant admin</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              label="Entity"
              value={filterEntity}
              onChange={(e: SelectChangeEvent) => {
                setFilterEntity(e.target.value);
                setPage(0);
              }}
              MenuProps={overlaySelectMenuProps.MenuProps}
            >
              <MenuItem value="all">All</MenuItem>
              {tenantEntities.map((e) => (
                <MenuItem key={e.entity_id} value={e.entity_id}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filterStatus}
              onChange={(e: SelectChangeEvent) => {
                setFilterStatus(e.target.value);
                setPage(0);
              }}
              MenuProps={overlaySelectMenuProps.MenuProps}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="removed">Removed from tenant</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={includeLegacyDoctors} onChange={(_, v) => setIncludeLegacyDoctors(v)} />}
            label="Legacy doctors"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showRemovedFromTenant}
                onChange={(_, v) => {
                  setShowRemovedFromTenant(v);
                  setPage(0);
                }}
              />
            }
            label="Show removed users"
          />
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" onClick={openAddDrawer} disabled={loading}>
            Add user
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell width={48} />
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Entities</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((r) => {
              const open = expandedUserId === r.user_id;
              const sel = selectedAssignmentIds[r.user_id] ?? new Set();
              return (
                <Fragment key={r.user_id}>
                  <TableRow hover selected={open}>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpandedUserId(open ? null : r.user_id)} aria-label="expand">
                        <Typography variant="caption">{open ? '▼' : '▶'}</Typography>
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: 14,
                            bgcolor: 'primary.main',
                          }}
                        >
                          {initials(r.display_name, r.username)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {r.display_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={isCustomTenantRole(r.tenant_role_name) ? 'Custom' : r.tenant_role_name || '—'}
                        sx={{ fontWeight: 500, ...roleChipColor(r.tenant_role_name || '') }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.from(
                          new Set((r.assignments ?? []).map((a) => a.entity_id).filter(Boolean)),
                        ).slice(0, 4).map((eid) => (
                          <Chip
                            key={eid}
                            size="small"
                            variant="outlined"
                            label={entityNameById.get(eid) ?? eid.slice(0, 8)}
                          />
                        ))}
                        {(r.assignments ?? []).length === 0 && <Typography variant="caption">—</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        {r.tenant_membership_active === false && (
                          <Chip size="small" color="warning" label="Removed from tenant" sx={{ fontWeight: 600 }} />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: r.is_active ? 'success.main' : 'action.disabled',
                            }}
                          />
                          <Typography variant="body2">
                            Account: {r.is_active ? 'Active' : 'Inactive'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuRow(r);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 0, bgcolor: 'grey.50' }}>
                      <Collapse in={open}>
                        <Box sx={{ py: 2, px: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Last login: {formatLastLogin(r.last_login_at)}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Permissions
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {(r.permission_codes ?? []).length ? (
                              r.permission_codes!.map((c) => <Chip key={c} size="small" label={c} variant="outlined" />)
                            ) : (
                              <Typography variant="caption">—</Typography>
                            )}
                          </Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Assignments
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Entity</TableCell>
                                <TableCell>Config</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(r.assignments ?? []).map((a) => (
                                <TableRow key={a.assignment_id}>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={sel.has(a.assignment_id)}
                                      onChange={() => toggleAssignmentSelect(r.user_id, a.assignment_id)}
                                    />
                                  </TableCell>
                                  <TableCell>{entityNameById.get(a.entity_id) ?? a.entity_id.slice(0, 8)}</TableCell>
                                  <TableCell>
                                    {a.config_id
                                      ? configById.get(a.config_id)?.name ?? a.config_id.slice(0, 8)
                                      : 'All configs'}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {!(r.assignments ?? []).length && (
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <Typography variant="caption">No assignments</Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                          {(r.assignments ?? []).length > 0 && (
                            <Button
                              size="small"
                              color="warning"
                              sx={{ mt: 1, textTransform: 'none' }}
                              disabled={sel.size === 0 || loading}
                              onClick={() => void removeAssignmentsBulk(r.user_id, Array.from(sel))}
                            >
                              Remove selected assignments
                            </Button>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
            {!pagedRows.length && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No users match filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        sx={{ zIndex: zAboveSettingsShell }}
      >
        <MenuItem
          onClick={() => {
            if (menuRow && !menuRow.legacy_no_tenant_membership) openEditDrawer(menuRow);
            setMenuAnchor(null);
          }}
          disabled={
            !menuRow ||
            !!menuRow?.legacy_no_tenant_membership ||
            menuRow.tenant_membership_active === false
          }
        >
          <ListItemText primary="Edit user" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) {
              setResetPwdRow(menuRow);
              setResetPwdValue('');
              setResetPwdOpen(true);
            }
            setMenuAnchor(null);
          }}
        >
          <ListItemText primary="Reset password" />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (menuRow && selectedTenantId) {
              try {
                await userAccessAPI.patchUser(menuRow.user_id, selectedTenantId, { is_active: !menuRow.is_active });
                setSuccess(menuRow.is_active ? 'User disabled.' : 'User enabled.');
                await load();
              } catch (e) {
                setError(String(e));
              }
            }
            setMenuAnchor(null);
          }}
          disabled={!menuRow || menuRow.tenant_membership_active === false}
        >
          <ListItemText primary={menuRow?.is_active ? 'Disable account' : 'Enable account'} />
        </MenuItem>
        {menuRow && menuRow.tenant_membership_active === false && (
          <MenuItem
            onClick={() => {
              setReactivateRow(menuRow);
              setReactivateOpen(true);
              setMenuAnchor(null);
            }}
          >
            <ListItemText primary="Restore to tenant" primaryTypographyProps={{ color: 'success.main' }} />
          </MenuItem>
        )}
        {isAppAdmin && menuRow && (
          <MenuItem onClick={() => void handleImpersonate(menuRow.user_id)}>
            <ListItemText primary="Impersonate (debug)" secondary="Admin only" />
          </MenuItem>
        )}
        <MenuItem disabled>
          <ListItemText
            primary="Audit log"
            secondary="Not implemented: needs audit table (who/when/action/target), append-only writes on UAM changes, and a read API or SIEM export"
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) {
              setDeleteRow(menuRow);
              setDeleteOpen(true);
            }
            setMenuAnchor(null);
          }}
          disabled={
            !menuRow ||
            !!menuRow?.legacy_no_tenant_membership ||
            menuRow.tenant_membership_active === false
          }
        >
          <ListItemText primary="Remove from tenant" primaryTypographyProps={{ color: 'error' }} />
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: false,
          sx: { zIndex: zAboveSettingsShell },
        }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 560 }, borderRadius: '12px 0 0 12px' } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {drawerMode === 'add' ? 'Add user' : 'Edit user'}
          </Typography>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
            {STEPS.map((s) => (
              <Step key={s}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
            {activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Username"
                  fullWidth
                  value={duUsername}
                  onChange={(e) => setDuUsername(e.target.value)}
                  disabled={drawerMode === 'edit'}
                />
                <TextField
                  label={drawerMode === 'add' ? 'Password' : 'New password (optional)'}
                  type="password"
                  fullWidth
                  value={duPassword}
                  onChange={(e) => setDuPassword(e.target.value)}
                />
                <TextField
                  label="Display name"
                  fullWidth
                  value={duDisplayName}
                  onChange={(e) => setDuDisplayName(e.target.value)}
                />
              </Box>
            )}

            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  select
                  label="Tenant role"
                  fullWidth
                  value={duTenantRoleName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDuTenantRoleName(v);
                    if (v !== 'Custom') applyRoleTemplateToPerms(v);
                  }}
                  helperText="Maps to RBAC role for this tenant. Choose Custom to tune permissions in step 4."
                  SelectProps={overlaySelectMenuProps}
                >
                  {templateRoleOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel control={<Switch checked={duActive} onChange={(_, v) => setDuActive(v)} />} label="Active" />
                <FormControlLabel
                  control={<Switch checked={duCanSettings} onChange={(_, v) => setDuCanSettings(v)} />}
                  label="Can open Settings"
                />
                <FormControlLabel
                  control={<Switch checked={duCanTest} onChange={(_, v) => setDuCanTest(v)} />}
                  label="Can open Test DHIS2"
                />
                <FormControlLabel
                  control={<Switch checked={duCanAdminPortal} onChange={(_, v) => setDuCanAdminPortal(v)} />}
                  label="Can open Admin Portal"
                />
              </Box>
            )}

            {activeStep === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {drawerMode === 'edit' && editRow && (
                  <>
                    <Typography variant="subtitle2">Add access to another entity</Typography>
                    <TextField
                      select
                      label="Entity"
                      fullWidth
                      value={addEntityId}
                      onChange={(e) => setAddEntityId(e.target.value)}
                      SelectProps={overlaySelectMenuProps}
                    >
                      {entities.map((e) => (
                        <MenuItem key={e.entity_id} value={e.entity_id}>
                          {e.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <FormControlLabel
                      control={<Switch checked={addAccessAll} onChange={(_, v) => setAddAccessAll(v)} />}
                      label="Access all configs for entity"
                    />
                    <Autocomplete
                      multiple
                      disabled={addAccessAll}
                      options={addEntityConfigs}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      getOptionLabel={(o) => o.name || o.id}
                      value={addConfigIds}
                      onChange={(_, v) => setAddConfigIds(v)}
                      renderInput={(params) => <TextField {...params} label="Configs" />}
                      {...overlayAutocompleteSlotProps}
                    />
                    <Button variant="outlined" onClick={() => void addEntityAccess()} disabled={loading}>
                      Add entity access
                    </Button>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Current entity access</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                      Uncheck to mark for removal, then click Apply.
                    </Typography>
                    {Array.from(new Set((editRow.assignments ?? []).map((a) => a.entity_id))).map((eid) => {
                      const entityAssignments = (editRow.assignments ?? []).filter((a) => a.entity_id === eid);
                      const allPending = entityAssignments.length > 0 && entityAssignments.every((a) => pendingRemovalIds.has(a.assignment_id));
                      const somePending = entityAssignments.some((a) => pendingRemovalIds.has(a.assignment_id));
                      const hasAllConfigs = entityAssignments.some((a) => !a.config_id);
                      return (
                        <Paper key={eid} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Checkbox
                              size="small"
                              checked={!allPending}
                              indeterminate={somePending && !allPending}
                              onChange={(_, checked) => {
                                setPendingRemovalIds((prev) => {
                                  const next = new Set(prev);
                                  if (!checked) entityAssignments.forEach((a) => next.add(a.assignment_id));
                                  else entityAssignments.forEach((a) => next.delete(a.assignment_id));
                                  return next;
                                });
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ textDecoration: allPending ? 'line-through' : 'none', color: allPending ? 'text.disabled' : 'text.primary' }}
                            >
                              {entityNameById.get(eid) ?? eid}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 1.5, py: 1 }}>
                            {hasAllConfigs ? (() => {
                              const asg = entityAssignments.find((a) => !a.config_id)!;
                              const isPending = pendingRemovalIds.has(asg.assignment_id);
                              return (
                                <Chip
                                  key={asg.assignment_id}
                                  size="small"
                                  label="All configs"
                                  variant="outlined"
                                  onClick={() => setPendingRemovalIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(asg.assignment_id)) next.delete(asg.assignment_id); else next.add(asg.assignment_id);
                                    return next;
                                  })}
                                  sx={{ textDecoration: isPending ? 'line-through' : 'none', opacity: isPending ? 0.45 : 1, cursor: 'pointer' }}
                                />
                              );
                            })() : entityAssignments.map((a) => {
                              const isPending = pendingRemovalIds.has(a.assignment_id);
                              const cfgName = a.config_id ? (configById.get(a.config_id)?.name ?? a.config_id.slice(0, 12)) : 'All';
                              return (
                                <Chip
                                  key={a.assignment_id}
                                  size="small"
                                  label={cfgName}
                                  variant="outlined"
                                  onClick={() => setPendingRemovalIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(a.assignment_id)) next.delete(a.assignment_id); else next.add(a.assignment_id);
                                    return next;
                                  })}
                                  sx={{ textDecoration: isPending ? 'line-through' : 'none', opacity: isPending ? 0.45 : 1, cursor: 'pointer' }}
                                />
                              );
                            })}
                          </Box>
                        </Paper>
                      );
                    })}
                    {pendingRemovalIds.size > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {pendingRemovalIds.size} item{pendingRemovalIds.size > 1 ? 's' : ''} marked for removal
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="text" sx={{ textTransform: 'none' }} onClick={() => setPendingRemovalIds(new Set())}>
                            Reset
                          </Button>
                          <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => void applyPendingRemovals()} disabled={loading}>
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                {/* Redundant after inline entity access cards — kept for add-mode saveDrawer payload, hidden in edit mode */}
                {drawerMode === 'add' && (
                  <>
                    <TextField
                      select
                      label="Entity"
                      fullWidth
                      value={duEntityId}
                      onChange={(e) => setDuEntityId(e.target.value)}
                      SelectProps={overlaySelectMenuProps}
                    >
                      {entities.map((e) => (
                        <MenuItem key={e.entity_id} value={e.entity_id}>
                          {e.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={duAccessAllConfigs}
                          onChange={(_, v) => {
                            setDuAccessAllConfigs(v);
                            if (v) setDuConfigIds([]);
                          }}
                        />
                      }
                      label="Access all configs for this entity"
                    />
                    <Autocomplete
                      multiple
                      disabled={duAccessAllConfigs}
                      options={duConfigOptions}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      getOptionLabel={(o) => o.name || o.id}
                      value={duConfigIds}
                      onChange={(_, v) => setDuConfigIds(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Configs" helperText="Pick specific adapters or enable all configs above" />
                      )}
                      {...overlayAutocompleteSlotProps}
                    />
                  </>
                )}
              </Box>
            )}

            {activeStep === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(['CONFIG', 'ENTITY', 'SYSTEM'] as const).map((grp) => (
                  <Box key={grp}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      {grp}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mt: 0.5 }}>
                      {groupedCatalog[grp].map((p) => (
                        <Box
                          key={p.code}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-of-type': { borderBottom: 'none' },
                          }}
                        >
                          <Switch
                            size="small"
                            checked={!!duPermEnabled[p.code]}
                            onChange={(_, v) => {
                              setDuPermEnabled((prev) => ({ ...prev, [p.code]: v }));
                              if (duTenantRoleName !== 'Custom') setDuTenantRoleName('Custom');
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {p.code.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {p.description || '—'}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {!groupedCatalog[grp].length && (
                        <Typography variant="caption" color="text.secondary">
                          No permissions in this group
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setDrawerOpen(false)}>Close</Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => Math.max(0, s - 1))}>
                Back
              </Button>
              {activeStep < STEPS.length - 1 ? (
                <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>
                  Next
                </Button>
              ) : (
                <Button variant="contained" onClick={() => void saveDrawer()} disabled={loading}>
                  Save
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} sx={{ zIndex: zAboveUserDrawer }}>
        <DialogTitle>Remove user from tenant?</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate <strong>{deleteRow?.username}</strong> in this tenant and revoke assignments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void confirmDelete()} disabled={loading}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reactivateOpen} onClose={() => setReactivateOpen(false)} sx={{ zIndex: zAboveUserDrawer }}>
        <DialogTitle>Restore user to tenant?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Reactivates <strong>{reactivateRow?.username}</strong> in this tenant: membership, entity assignments, and
            login (users.is_active) will be enabled again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateOpen(false)}>Cancel</Button>
          <Button color="success" variant="contained" onClick={() => void confirmReactivate()} disabled={loading}>
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: zAboveUserDrawer }}>
        <DialogTitle>Link legacy doctor</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info">Creates tenant membership and entity assignments.</Alert>
          <TextField label="Username" value={linkRow?.username ?? ''} disabled fullWidth />
          <TextField
            select
            label="Entity"
            fullWidth
            value={linkEntityId}
            onChange={(e) => setLinkEntityId(e.target.value)}
            SelectProps={overlaySelectMenuProps}
          >
            {entities.map((e) => (
              <MenuItem key={e.entity_id} value={e.entity_id}>
                {e.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Tenant role"
            fullWidth
            value={linkRoleName}
            onChange={(e) => setLinkRoleName(e.target.value as any)}
            SelectProps={overlaySelectMenuProps}
          >
            <MenuItem value="doctor">doctor</MenuItem>
            <MenuItem value="staff">staff</MenuItem>
            <MenuItem value="tenant_admin">tenant_admin</MenuItem>
          </TextField>
          <Autocomplete
            multiple
            options={linkEntityConfigs}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(o) => o.name || o.id}
            value={linkConfigIds}
            onChange={(_, v) => setLinkConfigIds(v)}
            renderInput={(params) => <TextField {...params} label="Configs" />}
            {...overlayAutocompleteSlotProps}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void confirmLinkLegacy()} disabled={loading}>
            Link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearAssignmentOpen} onClose={() => setClearAssignmentOpen(false)} sx={{ zIndex: zAboveUserDrawer }}>
        <DialogTitle>Remove entity access?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Removes all assignment rows for the primary entity selected in the drawer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAssignmentOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => void confirmClearEntityAssignment()} disabled={loading}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetPwdOpen} onClose={() => setResetPwdOpen(false)} sx={{ zIndex: zAboveUserDrawer }}>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New password"
            type="password"
            fullWidth
            value={resetPwdValue}
            onChange={(e) => setResetPwdValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPwdOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitResetPassword()} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {rows.some((r) => r.legacy_no_tenant_membership) && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Legacy doctors
          </Typography>
          <Table size="small">
            <TableBody>
              {rows
                .filter((r) => r.legacy_no_tenant_membership)
                .map((r) => (
                  <TableRow key={r.user_id}>
                    <TableCell>{r.username}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => { setLinkRow(r); setLinkOpen(true); }}>
                        Link
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
